import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, Trash2, GripVertical, CreditCard } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin "Metodi di pagamento" panel.
 *
 * Lets the host configure every payment method she accepts (Bonifico with
 * IBAN, PayPal link, Satispay number, PostePay, Cash, etc.). Only the active
 * ones are offered on the public booking page; the `account_detail` and
 * `instructions` fields are interpolated into the confirmation email so the
 * customer receives the right payment information for the method they chose.
 */

const KIND_OPTIONS = [
  { value: "bonifico", label: "Bonifico bancario", placeholder: "IBAN: IT00 X000 ..." },
  { value: "paypal", label: "PayPal", placeholder: "paypal.me/tuonome" },
  { value: "satispay", label: "Satispay", placeholder: "+39 333 0000000" },
  { value: "postepay", label: "PostePay", placeholder: "4023 6XXX XXXX XXXX" },
  { value: "sumup", label: "SumUp", placeholder: "Link SumUp" },
  { value: "carta", label: "Carta di credito (in struttura)", placeholder: "POS in struttura" },
  { value: "contanti", label: "Contanti", placeholder: "Alla consegna delle chiavi" },
  { value: "altro", label: "Altro", placeholder: "" },
];

const emptyForm = () => ({
  kind: "bonifico",
  label: "",
  active: true,
  sort_order: 99,
  holder_name: "",
  holder_tax_id: "",
  account_detail: "",
  instructions: "",
  notes_admin: "",
  qr_image: "",
});

export default function PaymentMethodsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = no edit, "new" = new, "<id>" = edit existing
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payment-methods");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Errore nel caricamento");
      console.warn("[payment-methods] load:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (pm) => {
    setEditing(pm.id);
    setForm({
      kind: pm.kind,
      label: pm.label,
      active: !!pm.active,
      sort_order: pm.sort_order ?? 99,
      holder_name: pm.holder_name || "",
      holder_tax_id: pm.holder_tax_id || "",
      account_detail: pm.account_detail || "",
      instructions: pm.instructions || "",
      notes_admin: pm.notes_admin || "",
      qr_image: pm.qr_image || "",
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm());
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast.error("Inserisci un'etichetta");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/payment-methods", form);
        toast.success("Metodo creato");
      } else {
        await api.put(`/admin/payment-methods/${editing}`, form);
        toast.success("Metodo aggiornato");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Errore nel salvataggio");
      console.warn("[payment-methods] save:", e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pm) => {
    if (!window.confirm(`Eliminare il metodo "${pm.label}"? Le prenotazioni passate non saranno modificate.`)) return;
    try {
      await api.delete(`/admin/payment-methods/${pm.id}`);
      toast.success("Metodo eliminato");
      await load();
    } catch (e) {
      toast.error("Errore");
      console.warn("[payment-methods] delete:", e);
    }
  };

  const toggleActive = async (pm) => {
    try {
      await api.put(`/admin/payment-methods/${pm.id}`, {
        ...pm,
        active: !pm.active,
      });
      await load();
    } catch (e) {
      toast.error("Errore");
      console.warn("[payment-methods] toggle:", e);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-stone-500">Caricamento…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Metodi di pagamento
          </h2>
          <p className="text-sm text-[#635F59]">
            Configura i metodi che vengono mostrati al cliente sul form di prenotazione.
            Le istruzioni qui inserite vengono incluse automaticamente nell'email di conferma.
          </p>
        </div>
        <Button onClick={startNew} className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white" data-testid="pm-new-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo metodo
        </Button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white border-2 border-[#AC5D45] rounded-xl p-5 space-y-4" data-testid="pm-edit-form">
          <h3 className="font-semibold text-[#1E3A5F]">
            {editing === "new" ? "Nuovo metodo" : "Modifica metodo"}
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="pm-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Etichetta visibile al cliente *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Es. Bonifico bancario"
                data-testid="pm-label"
              />
            </div>
            <div>
              <Label>Intestatario (opzionale)</Label>
              <Input
                value={form.holder_name}
                onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
                placeholder="Nome che appare sull'IBAN / PayPal"
                data-testid="pm-holder"
              />
            </div>
            <div>
              <Label>Codice Fiscale / P.IVA intestatario (opzionale)</Label>
              <Input
                value={form.holder_tax_id}
                onChange={(e) => setForm({ ...form, holder_tax_id: e.target.value.toUpperCase() })}
                placeholder="Es. RSSMRA80A01H501U — serve per ricariche PostePay / bonifico SEPA"
                maxLength={16}
                data-testid="pm-holder-tax-id"
              />
            </div>
            <div>
              <Label>Coordinate (IBAN / link / numero)</Label>
              <Input
                value={form.account_detail}
                onChange={(e) => setForm({ ...form, account_detail: e.target.value })}
                placeholder={KIND_OPTIONS.find((k) => k.value === form.kind)?.placeholder || ""}
                data-testid="pm-account"
              />
            </div>
          </div>

          <div>
            <Label>Istruzioni per il cliente</Label>
            <Textarea
              rows={3}
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="Es. 'Effettua il bonifico entro 24h dalla conferma. Inserisci come causale il codice prenotazione.'"
              data-testid="pm-instructions"
            />
            <p className="text-xs text-[#635F59] mt-1">
              Verranno mostrate nel form di prenotazione e nell'email di conferma.
            </p>
          </div>

          <div>
            <Label>Note private (solo per te)</Label>
            <Textarea
              rows={2}
              value={form.notes_admin}
              onChange={(e) => setForm({ ...form, notes_admin: e.target.value })}
              placeholder="Note interne — il cliente NON le vedrà"
              data-testid="pm-notes-admin"
            />
          </div>

          {/* QR Code upload — handy for Satispay / PayPal so the customer
              can scan directly from his phone and pay immediately. */}
          <div>
            <Label>Codice QR del pagamento (opzionale)</Label>
            <p className="text-xs text-[#635F59] mb-2">
              Carica l'immagine del tuo QR (es. il QR Satispay o PayPal). Il cliente lo vedrà nel form e nell'email — scansiona e paga al volo.
            </p>
            <div className="flex items-start gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 1.5 * 1024 * 1024) {
                    toast.error("Immagine troppo grande (max 1.5 MB). Riducila e riprova.");
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => {
                    setForm((prev) => ({ ...prev, qr_image: reader.result }));
                  };
                  reader.readAsDataURL(file);
                }}
                className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#AC5D45] file:text-white file:cursor-pointer"
                data-testid="pm-qr-upload"
              />
              {form.qr_image && (
                <div className="flex items-center gap-2">
                  <img
                    src={form.qr_image}
                    alt="QR preview"
                    className="w-20 h-20 border border-[#E8E2D9] rounded-lg object-contain bg-white"
                    data-testid="pm-qr-preview"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm({ ...form, qr_image: "" })}
                    data-testid="pm-qr-clear"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2 border-t border-[#E8E2D9]">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="pm-active"
              />
              <span>{form.active ? "Attivo (visibile al cliente)" : "Disattivato"}</span>
            </label>
            <div className="flex items-center gap-2 text-sm ml-auto" title="Numero più basso = appare più in alto nella lista del cliente">
              <Label className="mb-0">Posizione in lista:</Label>
              <Input
                type="number"
                className="w-20"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                data-testid="pm-sort-order"
              />
              <span className="text-xs text-[#635F59]">(1 = primo, 2 = secondo…)</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="pm-cancel">
              Annulla
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white" data-testid="pm-save">
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="bg-white border border-dashed border-[#E8E2D9] rounded-xl p-8 text-center text-[#635F59]">
            Nessun metodo di pagamento configurato. Aggiungine uno con "Nuovo metodo".
          </div>
        )}
        {items.map((pm) => (
          <div
            key={pm.id}
            className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${pm.active ? "border-[#E8E2D9]" : "border-stone-200 opacity-60"}`}
            data-testid={`pm-row-${pm.id}`}
          >
            <div className="text-stone-400 mt-1.5">
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#1E3A5F]">{pm.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{pm.kind}</span>
                {!pm.active && <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">Disattivato</span>}
                {pm.qr_image && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">QR</span>}
                <span className="text-xs text-stone-400 ml-auto" title="Posizione nella lista mostrata al cliente">Posizione: {pm.sort_order ?? 0}</span>
              </div>
              {pm.account_detail && (
                <div className="text-xs text-[#635F59] mt-1 font-mono break-all">{pm.account_detail}</div>
              )}
              {pm.instructions && (
                <div className="text-xs text-[#635F59] mt-1 italic">"{pm.instructions}"</div>
              )}
            </div>
            {pm.qr_image && (
              <img
                src={pm.qr_image}
                alt="QR"
                className="w-12 h-12 border border-[#E8E2D9] rounded object-contain bg-white"
              />
            )}
            <div className="flex items-center gap-1">
              <Switch
                checked={!!pm.active}
                onCheckedChange={() => toggleActive(pm)}
                data-testid={`pm-toggle-${pm.id}`}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => startEdit(pm)}
                data-testid={`pm-edit-${pm.id}`}
              >
                Modifica
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(pm)}
                data-testid={`pm-delete-${pm.id}`}
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
